import React from 'react';
import { node, object, oneOfType, string, bool, array } from 'prop-types';

import { Helmet } from 'react-helmet-async';

import { LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

// Global, but every selector inside is prefixed with .landingPage, so it cannot
// reach the Topbar, the Footer, or any other page. See the note at the top of
// the file and the test that enforces it.
import './landingPages.css';

/**
 * Shared shell for the /region/ and /kategorie/ landing pages.
 *
 * The whole point of this component: the header and footer are the app's real
 * ones, not the hand-written replica the static pages used to carry. A
 * signed-in visitor keeps their avatar and menu here, and the header can never
 * drift from the rest of the site because there is only one of it.
 *
 * `children` are wrapped in `.landingPage`, which is what scopes the marketing
 * stylesheet. The Topbar and Footer sit outside that wrapper and are therefore
 * untouched by it.
 *
 * @param {Object} props
 * @param {string} props.title document title
 * @param {string} props.description meta description
 * @param {Object|Array} [props.schema] JSON-LD, passed through to Page
 * @param {string} props.currentPage route name, for Topbar's active state
 * @param {boolean} props.scrollingDisabled
 * @param {boolean} [props.noindex] keep the page out of search results
 * @param {React.Node} props.children page sections
 */
const LandingLayout = props => {
  const { title, description, schema, currentPage, scrollingDisabled, noindex, children } = props;

  return (
    <Page
      title={title}
      description={description}
      schema={schema}
      scrollingDisabled={scrollingDisabled}
    >
      {/* The static pages carried `index, follow, max-image-preview:large`.
          index/follow is the default, but max-image-preview:large is not:
          without it these pages get small thumbnails in Google Search and
          Discover, which matters for pages whose content is supplier photos. */}
      <Helmet>
        <meta
          name="robots"
          content={noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large'}
        />
      </Helmet>
      <LayoutSingleColumn
        topbar={<TopbarContainer currentPage={currentPage} />}
        footer={<FooterContainer />}
      >
        <div className="landingPage">{children}</div>
      </LayoutSingleColumn>
    </Page>
  );
};

LandingLayout.propTypes = {
  title: string.isRequired,
  description: string,
  schema: oneOfType([object, array]),
  currentPage: string,
  scrollingDisabled: bool,
  noindex: bool,
  children: node,
};

export default LandingLayout;
